import {
	AgreementTypeEnum,
	CharterSubtype,
	CharterType,
	ContactRoleType,
	LocationType,
	RelationType,
	SalesCharterStatus,
	ShipNominationPeriodRegime,
} from "__generated__/enums";

import { Call, SalesCharter, SalesCharterContact } from "types/index";
import { VoyageSelectOption } from "components/selects/VoyageSelect";
import { ShipLinkDialogSalesCharterFragment } from "__generated__/view/charters/graphql/charter.fragments.generated";
import moment from "moment";

type CharterFormData = {
	id: number;
	charterNumber: string;
	type: CharterType;
	subtype: CharterSubtype;
	creditorId: number;
	debtorId: number;
	brokerId?: number;
	relationType?: RelationType;
	contacts?: SalesCharterContact[];
	minimumShipNominationDays: number | null;
	shipNominationPeriodRegime: ShipNominationPeriodRegime | null;
	shortNotice: boolean;
	customerNotes: string;
	internalNotes: string;
	customerReference: string;
	dossierNumber?: string;
	blDate?: number;
	contract?: { id?: number, termsAndConditions?: { id?: number } | null };
};

export const formatCharterForMutation = (data: CharterFormData) => {
	const createContactsInput = (contacts: SalesCharterContact[]) => {
		const arr = [] as { contactRoleType: ContactRoleType, contactIds: number[] }[];

		contacts.forEach(it => {
			const match = arr.find(a => a.contactRoleType === it.contactRoleType);

			if (match) {
				arr[arr.findIndex(a => a.contactRoleType === it.contactRoleType)] = {
					contactRoleType: match.contactRoleType,
					contactIds: [ ...match.contactIds, it.contact.id ],
				};
			} else {
				arr.push({
					contactRoleType: it.contactRoleType,
					contactIds: [ it.contact.id ],
				});
			}
		});

		return arr;
	};

	return {
		id: data.id ? data.id : undefined,
		charterNumber: data.charterNumber || undefined,
		creditor: {
			id: data.creditorId,
		},
		debtor: {
			id: data.debtorId,
		},
		contract: data.contract ? {
			id: data.contract.id,
			termsAndConditions: data.contract?.termsAndConditions ? {
				id: data.contract.termsAndConditions.id,
			} : undefined,
		} : undefined,
		type: data.type,
		subtype: data.subtype,
		relationType: data.relationType,
		broker: data.brokerId ? {
			id: data.brokerId,
		} : undefined,
		contacts: data.contacts != null ? createContactsInput(data.contacts) : undefined,
		minimumShipNominationDays: data.minimumShipNominationDays ? Number(data.minimumShipNominationDays) : undefined,
		shipNominationPeriodRegime: data.shipNominationPeriodRegime || undefined,
		shortNotice: data.shortNotice || false,
		customerNotes: data.customerNotes || undefined,
		internalNotes: data.internalNotes || undefined,
		customerReference: data.customerReference || undefined,
		dossierNumber: data.dossierNumber || undefined,
		blDate: data.blDate || undefined,
	};
};

export function canConfirmCharter(charter: SalesCharter) {
	const contacts = charter.contacts || [];
	const nominationAndConfirmationContacts = [ ...contacts ].filter(c => c.contactRoleType === ContactRoleType.NominationAndConfirmation);
	return charter.type === CharterType.Sales && (nominationAndConfirmationContacts.length > 0) && charter.cargos && charter.cargos.length > 0 && (charter.status && (charter.status.name !== SalesCharterStatus.Cancelled));
}

export function canPlanOnExistingArea(voyage: VoyageSelectOption | null | undefined, cargos: ShipLinkDialogSalesCharterFragment["cargos"]) {
	if (voyage?.calls) {
		const hasTerminalCall = voyage.calls.some(c => c.location?.type === LocationType.Terminal);

		const hasActionAndCallMatch = cargos.flatMap(cargo => cargo.actions)
			.some(action => (action.location?.type === LocationType.Terminal) && voyage.calls?.some(call => actionAndCallMatch(action, call)));

		return hasTerminalCall && hasActionAndCallMatch;
	} else {
		return false;
	}
}

const actionAndCallMatch = (
	action: {
		location?: { id: number } | null;
		start?: number | null;
	},
	call: Pick<Call, "pta" | "ptd"> & { location?: { id: number } | null },
) => {
	return action.location?.id === call.location?.id && (
		moment(action.start).isSame(moment(call.pta), "date") || moment(action.start).isSame(moment(call.ptd), "date")
	);
};

export const getAgreementShipFromSalesCharter = (charter: ShipLinkDialogSalesCharterFragment) => {
	if (charter.subtype !== CharterSubtype.Time) {
		return null;
	}

	const agreements = charter.contract?.agreements;

	if (agreements == null || agreements.length === 0) {
		return null;
	}

	type Agreements = NonNullable<NonNullable<ShipLinkDialogSalesCharterFragment["contract"]>["agreements"]>[number];
	type ShipAgreement = Extract<Agreements, { __typename?: "ShipAgreement" }>

	return agreements.find((agreement): agreement is ShipAgreement => agreement?.type === AgreementTypeEnum.Ship)?.ships?.[0] ?? null;
};
